import type { ConversationId } from "@redotech/effect-api-shared/ids/definitions";
import type { ConversationListItemResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/conversation";
import { useHandler } from "@redotech/react-util/hook";
import type { ConversationActionId } from "@redotech/redo-web/actions/conversation-actions/action-ids";
import { useMemo } from "react";
import {
  useArchiveConversationAction,
  useCloseConversationAction,
  useMarkUnreadConversationAction,
  useReopenConversationAction,
  type ConversationAction,
} from "../actions/use-conversation-actions";

export interface UseConversationActionToolbarResult {
  /** The primary close/reopen action based on current conversation status */
  primaryAction: ConversationAction;
  /** Additional menu actions (archive, mark unread, etc.) */
  menuActions: ConversationAction[];
  /** The conversation context for isEnabled/isVisible checks */
  conversation: ConversationListItemResponse;
  /** Execute the primary action */
  handlePrimaryAction: () => void;
  /** Execute a menu action by ID */
  handleMenuAction: (actionId: ConversationActionId) => void;
}

/**
 * Hook that provides conversation actions for a toolbar.
 * Actions include full metadata (icons, shortcuts, titles) and isEnabled/isVisible
 * checks with permissions already baked in.
 */
export function useConversationActionToolbar(
  conversationId: ConversationId,
  conversation: ConversationListItemResponse,
): UseConversationActionToolbarResult {
  // Get action hooks - these include isEnabled with permission checks
  const closeAction = useCloseConversationAction();
  const reopenAction = useReopenConversationAction();
  const markUnreadAction = useMarkUnreadConversationAction();
  const archiveAction = useArchiveConversationAction();

  const isClosed = conversation.status === "CLOSED";

  // Primary action: close or reopen based on current status
  const primaryAction = isClosed ? reopenAction : closeAction;

  // Menu actions
  const menuActions = useMemo(
    (): ConversationAction[] => [markUnreadAction, archiveAction],
    [markUnreadAction, archiveAction],
  );

  const handlePrimaryAction = useHandler(() => {
    const isEnabled = primaryAction.isEnabled?.(conversation) ?? true;
    if (isEnabled) {
      void primaryAction.execute(conversationId);
    }
  });

  const handleMenuAction = useHandler((actionId: ConversationActionId) => {
    const action = menuActions.find((a) => a.id === actionId);
    if (!action) {
      return;
    }
    const isEnabled = action.isEnabled?.(conversation) ?? true;
    if (isEnabled) {
      void action.execute(conversationId);
    }
  });

  return {
    primaryAction,
    menuActions,
    conversation,
    handlePrimaryAction,
    handleMenuAction,
  };
}
